import {MutableRefObject} from 'react';
import * as THREE from 'three';
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls';
import {PreviewPosition} from 'components/customer/Preview3D/types';
import TWEEN from '@tweenjs/tween.js';
import {USE_LEGACY_TWEEN_GROUP} from 'components/customer/Preview3DCommon/lib/tween';
import {store} from 'store/customer/storeSetup';
import {throttle} from 'lodash';
import {
    setHiddenWall,
    setIsViewingAtBottom,
    setPlannerView,
    setPreviewPosition,
    setTopViewZoom,
    setZoom,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {
    directionChanged,
    viewStateAfterDirectionChange,
} from 'components/customer/RoomPlanner/helpers/plannerView';
import {
    computeContainedOrthographicFrustum,
    tweenCameraPosition,
    tweenCameraZoom,
} from 'components/customer/Preview3DCommon/lib/cameraUtils';
import {isMobile} from 'react-device-detect';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';
import {findRoomFloor} from 'components/customer/RoomPlanner/helpers/roomFloorFootprint';
import {updateShadowsOnChange} from 'components/customer/RoomPlanner/helpers/shadows';
import {setupGlowLights} from 'components/customer/Preview3D/helpers/Three';

// OrbitControls internal _STATE values for ROTATE and TOUCH_ROTATE.
const ORBIT_CONTROLS_ROTATE_STATES = [0, 3];

// Right mouse button, as reported by PointerEvent.button.
const RIGHT_MOUSE_BUTTON = 2;

// How long the left button must stay down before the grab cursor shows, so
// a quick click (e.g. selecting a cabinet) never flashes it.
const ROTATE_CURSOR_DELAY_MS = 150;

// Set on the canvas while orbiting so hover handlers leave the cursor alone.
export const CAMERA_ROTATING_ATTRIBUTE = 'data-camera-rotating';

// How much of the room must stay on screen along each screen axis, as a
// fraction of the room's on-screen size or of the view, whichever is
// smaller: zoomed out, half the room stays visible; zoomed in, the room
// fills at least half the view.
export const PAN_MIN_VISIBLE_FRACTION = 0.5;

// Share of an out-of-bounds offset (after a zoom or orbit) corrected per
// frame, so the view eases back instead of jumping.
const PAN_BOUNDS_EASE = 0.2;

// Furthest the camera may zoom out (the default view is zoom 1).
const MIN_CAMERA_ZOOM = 0.4;

// Remaining pan (world units) below which the glide stops.
const PAN_INERTIA_EPSILON = 0.1;

// Visible height at zoom 1 on landscape screens.
const FRUSTUM_SIZE = 1500;

// The views and zoom levels are tuned for a landscape canvas, so at least
// this width / height stays visible. Narrower canvases (portrait) get extra
// space above and below instead of cropping the room's sides.
const MIN_VISIBLE_ASPECT = 1.5;

/**
 * Manages the setup and configuration of the camera, controls, and scene for
 * the 3D Room Planner. This class handles camera zoom, preview position, and
 * scene initialization.
 */
class CameraSetup3DRP {
    /**
     * The orthographic camera used in the scene.
     * @type {THREE.OrthographicCamera}
     */
    public camera: THREE.OrthographicCamera;

    /**
     * The orbit controls for manipulating the camera.
     * @type {OrbitControls}
     */
    public controls: OrbitControls;

    /**
     * The 3D scene being rendered.
     * @type {THREE.Scene}
     */
    public scene: THREE.Scene;

    /**
     * The current zoom level of the camera.
     * @type {number}
     * @private
     */
    private zoom: number;

    /**
     * The current preview position of the camera.
     * @type {PreviewPosition}
     * @private
     */
    private previewPosition: PreviewPosition;

    private lastCameraZone: string;

    private isViewingAtBottom = false;

    private lastRelativeX = 0;
    private lastRelativeZ = 0;

    private applyingPreset = false;
    private presetMoveToken = 0;
    private hasPresetDirection = false;
    private readonly presetDirection = new THREE.Vector3();
    private readonly currentDirection = new THREE.Vector3();

    private backgroundGradient: THREE.CanvasTexture;

    /**
     * Right-drag pan state, handled here instead of by OrbitControls so the
     * view stays within the room.
     * @private
     */
    private cameraPan = {
        active: false,
        pointerId: -1,
        lastClientX: 0,
        lastClientY: 0,
        // Pan still to apply, in world units along the camera's sideways (x)
        // and up (y) axes. Eased out a little every frame with the same
        // damping OrbitControls uses for orbiting, so it glides after release.
        pending: new THREE.Vector2(),
    };

    /**
     * Two-finger touch state: the fingers' last positions by pointer id, and
     * the distance between them for pinch zoom.
     * @private
     */
    private touchGesture = {
        points: new Map<number, {x: number; y: number}>(),
        lastDistance: 0,
    };

    /**
     * The room's bounds (floor and walls) that panning keeps on screen.
     * Cached because panning runs every frame; recomputed once the room is
     * replaced (a room rebuild clears the scene, detaching the old floor and
     * walls).
     * @private
     */
    private panBounds = {
        floor: null as THREE.Object3D | null,
        walls: null as THREE.Object3D | null,
        box: new THREE.Box3(),
    };

    /**
     * Scratch vectors reused by the per-frame pan maths.
     * @private
     */
    private panScratch = {
        step: new THREE.Vector2(),
        applied: new THREE.Vector2(),
        right: new THREE.Vector3(),
        up: new THREE.Vector3(),
        center: new THREE.Vector3(),
        halfSize: new THREE.Vector3(),
    };

    /**
     * Initializes the CameraSetup3DRP instance.
     * Subscribes to the Redux store and sets up the scene.
     */
    constructor() {
        if (store) {
            store.subscribe(this.watchState.bind(this) as () => void);
        }
        this.setupScene();
        // Throttle the checkCameraZone method
        this.checkCameraZone = throttle(this.checkCameraZone.bind(this), 100);
    }

    /**
     * Sets up the 3D scene, including camera and controls initialization.
     * @private
     */
    private setupScene() {
        if (this.scene) {
            this.scene.clear();
        } else {
            this.scene = new THREE.Scene();
            updateShadowsOnChange(this.scene);
        }
        this.scene.background = this.getBackgroundGradient();

        // Lights. Ambient is kept lower than before so the sun's shading and
        // shadows are visible; lit faces end up roughly as bright as before.
        const ambientLight = new THREE.AmbientLight(0xffffff, 1.4);
        this.scene.add(ambientLight);

        this.scene.add(this.createSunLight());
        setupGlowLights(this.scene);
    }

    /**
     * Returns the vertical gradient used as the scene background. A texture
     * background is drawn as a fullscreen quad, so the gradient stays fixed
     * to the viewport regardless of camera movement.
     * @private
     * @return {THREE.CanvasTexture | THREE.Color} The gradient, or plain
     * white where no 2D canvas is available (e.g. unit tests, since this
     * class is instantiated when the module is imported).
     */
    private getBackgroundGradient(): THREE.CanvasTexture | THREE.Color {
        if (this.backgroundGradient) return this.backgroundGradient;

        const canvas =
            typeof document !== 'undefined'
                ? document.createElement('canvas')
                : null;
        // Tests may stub document.createElement without canvas support.
        const context =
            typeof canvas?.getContext === 'function'
                ? canvas.getContext('2d')
                : null;
        if (!canvas || !context) return new THREE.Color(0xffffff);

        canvas.width = 2;
        canvas.height = 512;

        // Gradient is drawn at 30% opacity over white. Baked into the
        // canvas rather than using real transparency, so the result doesn't
        // depend on what sits behind the WebGL canvas (or in snapshots).
        context.fillStyle = '#FFFFFF';
        context.fillRect(0, 0, canvas.width, canvas.height);

        const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
        gradient.addColorStop(0, '#A1BECF');
        gradient.addColorStop(1, '#D6D5D0');
        context.globalAlpha = 0.3;
        context.fillStyle = gradient;
        context.fillRect(0, 0, canvas.width, canvas.height);

        this.backgroundGradient = new THREE.CanvasTexture(canvas);
        this.backgroundGradient.colorSpace = THREE.SRGBColorSpace;

        return this.backgroundGradient;
    }

    /**
     * Creates the shadow-casting directional "sun" light. The shadow camera
     * is sized to cover the largest rooms (scene units are mm * 0.2).
     * @private
     * @return {THREE.DirectionalLight}
     */
    private createSunLight() {
        const sunLight = new THREE.DirectionalLight(0xfff6e8, 1.8);
        // Same direction as the old fill light, pushed further out so the
        // shadow camera sits outside the room.
        sunLight.position.set(500, 1000, 700).setLength(3000);
        sunLight.target.position.set(0, 0, 0);

        sunLight.castShadow = true;
        const shadowMapSize = isMobile ? 2048 : 4096;
        sunLight.shadow.mapSize.set(shadowMapSize, shadowMapSize);
        sunLight.shadow.camera.left = -1500;
        sunLight.shadow.camera.right = 1500;
        sunLight.shadow.camera.top = 1500;
        sunLight.shadow.camera.bottom = -1500;
        sunLight.shadow.camera.near = 1;
        sunLight.shadow.camera.far = 6000;
        sunLight.shadow.bias = -0.0005;
        sunLight.shadow.normalBias = 0.5;
        sunLight.shadow.radius = 4;
        // Soften how dark shadowed areas get (1 = full shadow, 0 = none).
        sunLight.shadow.intensity = 0.3;

        return sunLight;
    }

    /**
     * Watches the application state and updates the camera position and zoom accordingly.
     * Synchronizes the camera setup with the Redux store state.
     * @private
     */
    private watchState(): void {
        const state = store.getState();

        if (state.roomPlanner.previewPosition != this.previewPosition) {
            this.previewPosition = state.roomPlanner.previewPosition;
            if (this.camera && state.roomPlanner.previewPosition) {
                this.setupCameraPosition(
                    state.roomPlanner.previewPosition,
                    state.roomPlanner.rectangularDimension.width // please use correct width here.
                );
            }
        }

        if (state.roomPlanner.zoom != this.zoom) {
            this.zoom = state.roomPlanner.zoom;
            if (this.camera) {
                this.setupZoomAndControls();
            }
        }
    }

    /**
     * Configures the camera zoom and updates the orbit controls based on the provided parameters.
     * Adjusts the camera's zoom level dynamically to fit the scene.
     *
     * @param {number} [zoomOverride] - Optional override for the zoom level.
     * @param {boolean} [topView] - Indicates if the camera should switch to a top-down view.
     * @param {boolean} [editHeightView] - Indicates if the camera should switch to an edit height view.
     * @param {boolean} [topWithoutEditing] - Indicates if the camera should switch to a top view without editing mode.
     * @param {number} [roomLength] - The length of the room used to calculate the appropriate zoom level.
     */
    private setupZoomAndControls(
        zoomOverride?: number,
        topView?: boolean,
        editHeightView?: boolean,
        topWithoutEditing?: boolean,
        roomLength?: number
    ) {
        // Adjust base zoom based on screen resolution
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const isLowResolution = screenWidth <= 1600 || screenHeight <= 900;
        const baseZoom = isLowResolution ? 0.5 : 0.72;

        const referenceLength = 8000;
        const calculatedZoom =
            baseZoom * (referenceLength / (roomLength ?? referenceLength));
        const topViewZoom = isMobile
            ? 0.32
            : Math.min(baseZoom, calculatedZoom) * 0.8;

        const targetZoom =
            topView || editHeightView || topWithoutEditing
                ? topViewZoom
                : zoomOverride || this.zoom;

        // OrbitControls clamps the zoom every frame, so the limit must allow
        // both the current zoom (while tweening) and the preset's own zoom,
        // which can be further out than MIN_CAMERA_ZOOM for long rooms.
        if (this.controls) {
            this.controls.minZoom = Math.min(
                MIN_CAMERA_ZOOM,
                this.camera.zoom,
                targetZoom
            );
        }

        tweenCameraZoom(this.camera, targetZoom, 500, () => {
            if (this.controls) {
                this.controls.minZoom = Math.min(MIN_CAMERA_ZOOM, targetZoom);
            }
            store.dispatch(setPreviewPosition(null));
        });

        if (this.controls) {
            if (topView || topWithoutEditing) {
                this.controls.target.set(13, 0, 0);
                if (topView) {
                    this.controls.enabled = false;
                    this.controls.enableRotate = false;
                    this.controls.enableZoom = false;
                    this.controls.enablePan = false;
                } else {
                    this.controls.enabled = true;
                    this.controls.enableRotate = true;
                    this.controls.enableZoom = true;
                    this.controls.enablePan = true;
                }

                store.dispatch(setTopViewZoom(topViewZoom));
            } else {
                new TWEEN.Tween(this.controls.target, USE_LEGACY_TWEEN_GROUP)
                    .to({x: 15, y: 113, z: 75}, 500)
                    .easing(TWEEN.Easing.Cubic.Out)
                    .start();
                if (!editHeightView) {
                    this.controls.enabled = true;
                    this.controls.enableRotate = true;
                    this.controls.enableZoom = true;
                    this.controls.enablePan = true;
                } else {
                    this.controls.enabled = false;
                    this.controls.enableRotate = false;
                    this.controls.enableZoom = false;
                    this.controls.enablePan = false;
                    store.dispatch(setTopViewZoom(topViewZoom));
                }
            }
        }
    }

    /**
     * Sets up the camera for rendering, including its dimensions and aspect ratio.
     * Initializes the camera and its controls.
     * @param {MutableRefObject<HTMLDivElement>} mountRef - The reference to the HTML element where the camera is mounted.
     * @param {THREE.WebGLRenderer} renderer - The WebGL renderer used for rendering the scene.
     * @return {THREE.OrthographicCamera | undefined} - The initialized orthographic camera.
     */
    public setupCamera(
        mountRef: MutableRefObject<HTMLDivElement>,
        renderer: THREE.WebGLRenderer
    ) {
        if (mountRef.current) {
            const dimension = mountRef.current.getBoundingClientRect();

            this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 1, 20000);
            this.applyViewportFrustum(dimension.width, dimension.height);
            this.camera.zoom = this.zoom;
            this.camera.lookAt(0, 0, 0);

            this.setupScene();

            this.controls = new OrbitControls(this.camera, renderer.domElement);

            // Smooth out orbiting/panning with inertia. Requires
            // controls.update() on every frame, which the render loop does.
            this.controls.enableDamping = true;
            this.controls.dampingFactor = 0.05;
            this.controls.rotateSpeed = 0.4;
            this.controls.zoomSpeed = 0.6;
            this.controls.minZoom = Math.min(
                MIN_CAMERA_ZOOM,
                this.camera.zoom || MIN_CAMERA_ZOOM
            );

            // Keep the camera at or above floor level so it never looks up
            // from underneath the floor.
            this.controls.minPolarAngle = 0;
            this.controls.maxPolarAngle = Math.PI / 2;

            const controls = this.controls;

            // Show a grab cursor while rotating (left-click drag), debounced
            // so clicks don't flash it. OrbitControls sets its (untyped)
            // internal state before dispatching 'start'.
            const domElement = renderer.domElement;
            let rotateCursorTimer: ReturnType<typeof setTimeout> | null = null;

            controls.addEventListener('start', () => {
                const {state} = controls as OrbitControls & {state: number};
                if (!ORBIT_CONTROLS_ROTATE_STATES.includes(state)) return;

                if (rotateCursorTimer) clearTimeout(rotateCursorTimer);
                rotateCursorTimer = setTimeout(() => {
                    rotateCursorTimer = null;
                    // Dragging a cabinet disables the controls mid-press.
                    if (!controls.enabled) return;

                    domElement.setAttribute(CAMERA_ROTATING_ATTRIBUTE, '');
                    domElement.style.cursor = 'grab';
                }, ROTATE_CURSOR_DELAY_MS);
            });
            controls.addEventListener('end', () => {
                if (rotateCursorTimer) {
                    clearTimeout(rotateCursorTimer);
                    rotateCursorTimer = null;
                }

                if (domElement.hasAttribute(CAMERA_ROTATING_ATTRIBUTE)) {
                    domElement.removeAttribute(CAMERA_ROTATING_ATTRIBUTE);
                    domElement.style.cursor = '';
                }
            });

            this.setupCameraPan(domElement);

            return this.camera;
        }
    }

    /**
     * Fits the camera to a new canvas size, e.g. after rotating between
     * portrait and landscape. Zoom, orbit and pan are kept; keepRoomInView
     * eases the room back on screen if the narrower view left too little.
     * @param {number} width - Canvas width in pixels.
     * @param {number} height - Canvas height in pixels.
     */
    public resizeViewport(width: number, height: number) {
        if (!this.camera) return;

        this.applyViewportFrustum(width, height);
        this.camera.updateProjectionMatrix();
    }

    /**
     * Sets the camera's frustum for the given canvas size.
     * @param {number} width - Canvas width in pixels.
     * @param {number} height - Canvas height in pixels.
     * @private
     */
    private applyViewportFrustum(width: number, height: number) {
        // Hidden or not laid out yet; keep the last frustum.
        if (width <= 0 || height <= 0) return;

        const {left, right, top, bottom} = computeContainedOrthographicFrustum(
            width,
            height,
            FRUSTUM_SIZE,
            MIN_VISIBLE_ASPECT
        );

        this.camera.left = left;
        this.camera.right = right;
        this.camera.top = top;
        this.camera.bottom = bottom;
    }

    /**
     * Replaces OrbitControls' right-drag and two-finger pan: the scene
     * follows the cursor / fingers sideways and up / down, the view can't
     * leave the room, and it eases in and glides to a stop on release like
     * orbiting. Pinch zoom comes along with the two-finger pan.
     * @param {HTMLElement} domElement - The renderer's canvas.
     * @private
     */
    private setupCameraPan(domElement: HTMLElement) {
        const controls = this.controls;

        // Take right-drag and two-finger gestures away from OrbitControls
        // (null = no action). One finger still orbits.
        controls.mouseButtons.RIGHT = null;
        controls.touches.TWO = null;

        this.setupTouchGesture(domElement);

        domElement.addEventListener('pointerdown', (event) => {
            // Top / edit-height views disable panning via enablePan.
            if (
                event.button !== RIGHT_MOUSE_BUTTON ||
                event.pointerType !== 'mouse' ||
                !controls.enablePan
            ) {
                return;
            }

            const pan = this.cameraPan;
            pan.active = true;
            pan.pointerId = event.pointerId;
            pan.lastClientX = event.clientX;
            pan.lastClientY = event.clientY;

            domElement.setPointerCapture(event.pointerId);
        });

        domElement.addEventListener('pointermove', (event) => {
            const pan = this.cameraPan;
            if (!pan.active || event.pointerId !== pan.pointerId) return;

            this.addScreenPan(
                event.clientX - pan.lastClientX,
                event.clientY - pan.lastClientY,
                domElement
            );
            pan.lastClientX = event.clientX;
            pan.lastClientY = event.clientY;
        });

        const endPan = (event: PointerEvent) => {
            const pan = this.cameraPan;
            if (!pan.active || event.pointerId !== pan.pointerId) return;

            pan.active = false;
            if (domElement.hasPointerCapture(event.pointerId)) {
                domElement.releasePointerCapture(event.pointerId);
            }
            // Whatever is still pending glides out in the render loop.
        };
        domElement.addEventListener('pointerup', endPan);
        domElement.addEventListener('pointercancel', endPan);

        // The render loop calls controls.update() every frame; ease the pan
        // in from there.
        const orbitUpdate = controls.update.bind(controls) as (
            deltaTime?: number | null
        ) => boolean;
        controls.update = (deltaTime?: number | null) => {
            this.updateCameraPan();
            this.keepRoomInView();
            return orbitUpdate(deltaTime);
        };
    }

    /**
     * Two fingers pan (following their midpoint) and pinch to zoom. Handled
     * here rather than by OrbitControls, which ignores a pinch whose first
     * finger landed while it was disabled (pressing on the selected cabinet
     * disables it for a cabinet drag).
     * @param {HTMLElement} domElement - The renderer's canvas.
     * @private
     */
    private setupTouchGesture(domElement: HTMLElement) {
        const {points} = this.touchGesture;

        const fingerDistance = () => {
            const [a, b] = Array.from(points.values());
            return Math.hypot(a.x - b.x, a.y - b.y);
        };

        domElement.addEventListener('pointerdown', (event) => {
            if (event.pointerType !== 'touch') return;

            points.set(event.pointerId, {x: event.clientX, y: event.clientY});
            if (points.size === 2) {
                this.touchGesture.lastDistance = fingerDistance();
            }
        });

        domElement.addEventListener('pointermove', (event) => {
            const point = points.get(event.pointerId);
            if (!point) return;

            const deltaX = event.clientX - point.x;
            const deltaY = event.clientY - point.y;
            point.x = event.clientX;
            point.y = event.clientY;

            // Three or more fingers do nothing.
            if (points.size !== 2) return;

            // Top / edit-height views turn these off via the controls.
            const {controls} = this;
            if (controls.enablePan) {
                // One finger moving shifts the midpoint by half as much.
                this.addScreenPan(deltaX / 2, deltaY / 2, domElement);
            }

            const distance = fingerDistance();
            const {lastDistance} = this.touchGesture;
            if (controls.enableZoom && lastDistance > 0 && distance > 0) {
                this.camera.zoom = THREE.MathUtils.clamp(
                    this.camera.zoom * (distance / lastDistance),
                    controls.minZoom,
                    controls.maxZoom
                );
                this.camera.updateProjectionMatrix();
                controls.dispatchEvent({type: 'change'});
            }
            this.touchGesture.lastDistance = distance;
        });

        const endTouch = (event: PointerEvent) => {
            if (!points.delete(event.pointerId)) return;

            // Back to two fingers (after a third lifted): pinch from here.
            this.touchGesture.lastDistance =
                points.size === 2 ? fingerDistance() : 0;
        };
        domElement.addEventListener('pointerup', endTouch);
        domElement.addEventListener('pointercancel', endTouch);
    }

    /**
     * Queues a pan that moves the scene by the given screen distance, so it
     * follows the cursor / fingers.
     * @param {number} deltaX - Horizontal screen movement in pixels.
     * @param {number} deltaY - Vertical screen movement in pixels.
     * @param {HTMLElement} domElement - The renderer's canvas.
     * @private
     */
    private addScreenPan(
        deltaX: number,
        deltaY: number,
        domElement: HTMLElement
    ) {
        // Orthographic, so a pixel is the same world distance everywhere.
        const {camera} = this;
        const worldPerPixelX =
            (camera.right - camera.left) / camera.zoom / domElement.clientWidth;
        const worldPerPixelY =
            (camera.top - camera.bottom) /
            camera.zoom /
            domElement.clientHeight;

        this.cameraPan.pending.x -= deltaX * worldPerPixelX;
        this.cameraPan.pending.y += deltaY * worldPerPixelY;
    }

    /**
     * Per-frame pan step, damped like OrbitControls' orbit: applies a
     * dampingFactor share of the pending pan and keeps the rest for the
     * following frames.
     * @private
     */
    private updateCameraPan() {
        const {pending} = this.cameraPan;

        if (pending.lengthSq() < PAN_INERTIA_EPSILON ** 2) {
            pending.set(0, 0);
            return;
        }

        const {controls} = this;
        const share = controls.enableDamping ? controls.dampingFactor : 1;
        const step = this.panScratch.step.copy(pending).multiplyScalar(share);
        const applied = this.applyCameraPan(step);

        // Drop the part a bound blocked, so the pan stops at it instead of
        // pushing against it.
        pending.x =
            Math.abs(applied.x - step.x) > 1e-6 ? 0 : pending.x - step.x;
        pending.y =
            Math.abs(applied.y - step.y) > 1e-6 ? 0 : pending.y - step.y;
    }

    /**
     * Finds the room floor mesh, if the room has been built.
     * @private
     * @return {THREE.Object3D | undefined}
     */
    private getFloor() {
        return findRoomFloor(this.scene);
    }

    /**
     * Returns the room's bounds (floor and walls), or null before the
     * room's floor has been built.
     * @private
     * @return {THREE.Box3 | null}
     */
    private getPanBounds() {
        const {panBounds} = this;

        if (
            panBounds.floor?.parent !== this.scene ||
            panBounds.walls?.parent !== this.scene
        ) {
            const floor = this.getFloor();
            if (!floor) return null;

            panBounds.floor = floor;
            panBounds.walls =
                this.scene.getObjectByName(ObjectNameMap.rectangularWall) ??
                this.scene.getObjectByName(ObjectNameMap.lShapeWall) ??
                null;

            panBounds.box.setFromObject(floor);
            if (panBounds.walls) {
                panBounds.box.union(
                    new THREE.Box3().setFromObject(panBounds.walls)
                );
            }
        }

        return panBounds.box;
    }

    /**
     * How far the view's centre may move along one screen axis while enough
     * of the room stays on screen (see PAN_MIN_VISIBLE_FRACTION).
     * @param {THREE.Box3} room - The room's bounds.
     * @param {THREE.Vector3} axis - The screen axis in world space (unit).
     * @param {number} halfView - Half the visible extent along `axis`.
     * @private
     * @return {{min: number, max: number}} Allowed movement along `axis`
     * from the current centre; min > 0 or max < 0 when the centre is
     * already out of bounds.
     */
    private getPanLimitsAlong(
        room: THREE.Box3,
        axis: THREE.Vector3,
        halfView: number
    ) {
        const {center, halfSize} = this.panScratch;
        room.getCenter(center);
        room.getSize(halfSize).multiplyScalar(0.5);

        // The box's extent projected onto the axis, i.e. its on-screen size
        // along that axis (orthographic, so independent of depth).
        const middle = center.dot(axis);
        const reach =
            Math.abs(axis.x) * halfSize.x +
            Math.abs(axis.y) * halfSize.y +
            Math.abs(axis.z) * halfSize.z;
        const current = this.controls.target.dot(axis);

        // The view [c - halfView, c + halfView] and room [m - reach,
        // m + reach] overlap by at least `minOverlap` while |c - m| stays
        // within `slack`.
        const minOverlap =
            PAN_MIN_VISIBLE_FRACTION * 2 * Math.min(reach, halfView);
        const slack = reach + halfView - minOverlap;

        return {
            min: middle - slack - current,
            max: middle + slack - current,
        };
    }

    /**
     * The camera's sideways and up axes in world space, and half the
     * visible width / height at the current zoom.
     * @private
     * @return {object} The screen axes and half extents.
     */
    private getScreenAxes() {
        const {camera, panScratch} = this;

        camera.updateMatrixWorld();
        return {
            right: panScratch.right
                .setFromMatrixColumn(camera.matrixWorld, 0)
                .normalize(),
            up: panScratch.up
                .setFromMatrixColumn(camera.matrixWorld, 1)
                .normalize(),
            halfWidth: (camera.right - camera.left) / 2 / camera.zoom,
            halfHeight: (camera.top - camera.bottom) / 2 / camera.zoom,
        };
    }

    /**
     * Moves the camera and orbit target together along the given world
     * axes.
     * @param {THREE.Vector3} right - The camera's sideways axis.
     * @param {number} rightAmount - Movement along `right`.
     * @param {THREE.Vector3} up - The camera's up axis.
     * @param {number} upAmount - Movement along `up`.
     * @private
     */
    private moveView(
        right: THREE.Vector3,
        rightAmount: number,
        up: THREE.Vector3,
        upAmount: number
    ) {
        for (const point of [this.controls.target, this.camera.position]) {
            point
                .addScaledVector(right, rightAmount)
                .addScaledVector(up, upAmount);
        }
    }

    /**
     * Moves the camera and orbit target together along the camera's
     * sideways and up axes, keeping enough of the room on screen. An
     * orthographic view only shifts on screen, so how far it may go scales
     * with the zoom.
     * @param {THREE.Vector2} step - Desired movement in world units along
     * the camera's sideways (x) and up (y) axes.
     * @private
     * @return {THREE.Vector2} The movement actually applied after clamping,
     * along the same axes.
     */
    private applyCameraPan(step: THREE.Vector2) {
        const {right, up, halfWidth, halfHeight} = this.getScreenAxes();

        let rightAmount = step.x;
        let upAmount = step.y;

        const room = this.getPanBounds();
        if (room) {
            // Never push the centre further out if it already starts
            // outside (e.g. after zooming in); keepRoomInView eases it back.
            const clampAlong = (
                amount: number,
                axis: THREE.Vector3,
                halfView: number
            ) => {
                const {min, max} = this.getPanLimitsAlong(room, axis, halfView);
                return THREE.MathUtils.clamp(
                    amount,
                    Math.min(0, min),
                    Math.max(0, max)
                );
            };

            rightAmount = clampAlong(rightAmount, right, halfWidth);
            upAmount = clampAlong(upAmount, up, halfHeight);
        }

        this.moveView(right, rightAmount, up, upAmount);

        return this.panScratch.applied.set(rightAmount, upAmount);
    }

    /**
     * Per-frame: eases the view back once zooming in or orbiting has left
     * too little of the room on screen. Skipped where panning is off (top /
     * edit-height views), which place the camera themselves.
     * @private
     */
    private keepRoomInView() {
        if (!this.controls.enablePan) return;

        const room = this.getPanBounds();
        if (!room) return;

        const {right, up, halfWidth, halfHeight} = this.getScreenAxes();

        // Movement back within the limits (0 when already within them).
        const correctionAlong = (axis: THREE.Vector3, halfView: number) => {
            const {min, max} = this.getPanLimitsAlong(room, axis, halfView);
            const correction = THREE.MathUtils.clamp(0, min, max);
            return Math.abs(correction) < PAN_INERTIA_EPSILON
                ? correction
                : correction * PAN_BOUNDS_EASE;
        };

        const rightAmount = correctionAlong(right, halfWidth);
        const upAmount = correctionAlong(up, halfHeight);
        if (rightAmount === 0 && upAmount === 0) return;

        this.moveView(right, rightAmount, up, upAmount);
    }

    /**
     * Sets the camera position based on the specified preview position and room length.
     * Adjusts the camera's position and zoom dynamically.
     * @param {PreviewPosition} position - The desired preview position for the camera.
     * @param {number} roomLength - The length of the room used for zoom calculations.
     */
    public setupCameraPosition(position: PreviewPosition, roomLength: number) {
        this.beginPresetTransition();
        const cameraPosition = {x: 0, y: 0, z: 0} as {
            x: number;
            y: number;
            z: number;
        };

        const isTopWithoutEditing = position === 'TOP_WITHOUT_EDITING';
        const isTop = position === 'TOP';
        const isLeftLeveledStatic = position === 'LEFT_LEVELED_STATIC';

        this.flushControlsInertia();

        switch (position) {
            case 'RIGHT':
                cameraPosition.x = 1750;
                cameraPosition.y = 1423;
                cameraPosition.z = 2695;
                break;
            case 'LEFT_LEVELED':
            case 'LEFT_LEVELED_STATIC':
                cameraPosition.x = -1583;
                cameraPosition.y = 113;
                cameraPosition.z = 75;
                break;
            case 'FRONT':
                cameraPosition.x = 15;
                cameraPosition.y = 113;
                cameraPosition.z = 2040;
                break;
            case 'RIGHT_LEVELED':
                cameraPosition.x = 1980;
                cameraPosition.y = 113;
                cameraPosition.z = 75;
                break;
            case 'TOP':
            case 'TOP_WITHOUT_EDITING':
                cameraPosition.x = 13;
                cameraPosition.y = 2000;
                cameraPosition.z = 0;
                break;
            default:
                cameraPosition.x = -1750;
                cameraPosition.y = 1423;
                cameraPosition.z = 2695;
                break;
        }

        if (isTop || isTopWithoutEditing) {
            this.camera.position.set(
                cameraPosition.x,
                cameraPosition.y,
                cameraPosition.z
            );
        } else {
            tweenCameraPosition(this.camera, cameraPosition);
        }

        this.setupZoomAndControls(
            1,
            isTop,
            isLeftLeveledStatic,
            isTopWithoutEditing,
            roomLength
        );
        store.dispatch(setZoom(1));
    }

    /**
     * Applies and clears any pending damping momentum so it doesn't drift
     * the camera after it is positioned programmatically. OrbitControls keeps
     * applying damping in update() even while the controls are disabled.
     * @private
     */
    private flushControlsInertia() {
        this.cameraPan.pending.set(0, 0);

        if (!this.controls || !this.controls.enableDamping) return;

        this.controls.enableDamping = false;
        this.controls.update();
        this.controls.enableDamping = true;
    }

    /**
     * Ignores camera motion caused by a preset tween so only a real orbit
     * clears the highlighted view.
     */
    private beginPresetTransition() {
        this.applyingPreset = true;
        this.hasPresetDirection = false;
        this.presetMoveToken += 1;
        const token = this.presetMoveToken;
        window.setTimeout(() => {
            if (this.presetMoveToken - token === 0) {
                this.applyingPreset = false;
            }
        }, 700);
    }

    /**
     * Clears the highlighted preset when the camera direction changes.
     * Pan and zoom keep the same direction, so they leave the highlight.
     */
    private syncViewPresetHighlight() {
        if (!this.camera || !this.controls || this.applyingPreset) return;

        const state = store.getState().roomPlanner;
        if (
            state.isEditingRoomDimension ||
            state.isTopView ||
            state.isEditHeightView
        ) {
            return;
        }

        this.currentDirection
            .copy(this.camera.position)
            .sub(this.controls.target);
        if (this.currentDirection.lengthSq() < 1e-6) return;

        if (!this.hasPresetDirection) {
            this.presetDirection.copy(this.currentDirection).normalize();
            this.hasPresetDirection = true;
            return;
        }

        this.currentDirection.normalize();
        if (
            !directionChanged(this.presetDirection.dot(this.currentDirection))
        ) {
            return;
        }

        this.hasPresetDirection = false;
        const next = viewStateAfterDirectionChange(
            state.plannerViewMode,
            state.activeViewPreset
        );
        if (next) {
            store.dispatch(setPlannerView(next));
        }
    }

    /**
     * Checks the camera's position to show/hide walls and also floor based on
     * vertical camera position
     */
    public checkCameraZone() {
        this.syncViewPresetHighlight();
        if (!this.camera) return;
        const {x, z} = this.camera.position;
        let newZone = '';

        const absX = Math.abs(x);
        const absZ = Math.abs(z);

        // Add minimum threshold to prevent flickering on very small movements
        const MIN_POSITION_CHANGE = 50;
        if (absX < MIN_POSITION_CHANGE && absZ < MIN_POSITION_CHANGE) {
            return;
        }

        // Get the camera's target position (where we're looking at)
        const targetX = this.controls.target.x;
        const targetZ = this.controls.target.z;

        // Calculate the relative position to the target
        const relativeX = x - targetX;
        const relativeZ = z - targetZ;
        const absRelativeX = Math.abs(relativeX);
        const absRelativeZ = Math.abs(relativeZ);

        // Calculate ratios in both directions with wider thresholds
        const ratioXtoZ = absRelativeX / absRelativeZ;
        const ratioZtoX = absRelativeZ / absRelativeX;
        const isSlightTilt =
            (ratioXtoZ > 0.15 && ratioXtoZ < 0.85) ||
            (ratioZtoX > 0.15 && ratioZtoX < 0.85);

        // Determine primary and secondary walls based on position
        let primaryWall = '';
        let secondaryWall = '';

        // Determine which axis is dominant with a buffer zone
        const DOMINANCE_THRESHOLD = 1.2; // 20% difference required to consider an axis dominant
        const isXDominant = absRelativeX > absRelativeZ * DOMINANCE_THRESHOLD;
        const isZDominant = absRelativeZ > absRelativeX * DOMINANCE_THRESHOLD;

        // Check if we're in a dragging operation by checking if controls are enabled
        const isDragging = this.controls.enabled;

        if (isXDominant) {
            // X is dominant, so it's the primary wall
            if (relativeX > 0) {
                primaryWall = 'rightWall';
            } else {
                primaryWall = 'leftWall';
            }
            // Z becomes secondary
            if (relativeZ > 0) {
                secondaryWall = 'frontWall';
            } else {
                secondaryWall = 'backWall';
            }
        } else if (isZDominant) {
            // Z is dominant, so it's the primary wall
            if (relativeZ > 0) {
                primaryWall = 'frontWall';
            } else {
                primaryWall = 'backWall';
            }
            // X becomes secondary
            if (relativeX > 0) {
                secondaryWall = 'rightWall';
            } else {
                secondaryWall = 'leftWall';
            }
        } else {
            // If neither axis is clearly dominant, use the current zone
            return;
        }

        // If we have a slight tilt, combine both walls
        if (isSlightTilt) {
            newZone = `${primaryWall},${secondaryWall}`;
        } else {
            newZone = primaryWall;
        }

        // Only update wall state if we're not dragging or if the change is significant
        if (newZone && newZone !== this.lastCameraZone) {
            // During dragging, only update if the change is significant
            if (isDragging) {
                const significantChange =
                    Math.abs(absRelativeX - this.lastRelativeX) >
                        MIN_POSITION_CHANGE ||
                    Math.abs(absRelativeZ - this.lastRelativeZ) >
                        MIN_POSITION_CHANGE;

                if (!significantChange) {
                    return;
                }
            }

            this.lastCameraZone = newZone;
            this.lastRelativeX = absRelativeX;
            this.lastRelativeZ = absRelativeZ;
            store.dispatch(setHiddenWall(this.lastCameraZone));
        }

        // Measured from the orbit target, not the floor: panning an
        // orthographic view down takes the camera below the floor without
        // ever looking at it from underneath.
        const heightAboveTarget =
            this.camera.position.y - this.controls.target.y;
        if (heightAboveTarget < -300 && !this.isViewingAtBottom) {
            this.isViewingAtBottom = true;
            store.dispatch(setIsViewingAtBottom(true));
        } else if (heightAboveTarget >= -300 && this.isViewingAtBottom) {
            this.isViewingAtBottom = false;
            store.dispatch(setIsViewingAtBottom(false));
        }
    }
}

export default new CameraSetup3DRP();
